// src/services/apiService.js
import { apiConfig } from '../auth/authConfig';
import logger from '../utils/logger';

class ApiService {
  constructor() {
    this.baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://demo-backend-demo.uksouth-01.azurewebsites.net/api';
  }
  
  // Helper method for making requests with proper credentials
  async fetchWithCredentials(endpoint, options = {}) {
    try {
      const url = endpoint.startsWith('http') ? endpoint : `${this.baseUrl}${endpoint}`;
      
      // Ensure credentials are included for cookies
      const enhancedOptions = {
        ...options,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...options.headers
        }
      };
      
      if (process.env.NODE_ENV === 'development') {
        logger.log(`Making API call to: ${url}`);
      }
      
      const response = await fetch(url, enhancedOptions);
      
      // Handle non-ok responses
      if (!response.ok) {
        // 401 means the session is gone — notify AuthContext to redirect to login
        if (response.status === 401) {
          window.dispatchEvent(new CustomEvent('authRequired'));
        }
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.message || errorData.error || `API Error: ${response.status}`;
        throw {
          message: errorMessage,
          response: {
            status: response.status,
            data: errorData
          }
        };
      }
      
      // Try to parse as JSON, but if it fails, return the response directly
      try {
        return await response.json();
      } catch (e) {
        return { success: true, message: 'Operation completed successfully' };
      }
    } catch (error) {
      logger.error(`Error in API call: ${error.message || error}`);
      throw error;
    }
  }
  
  // Generic GET method
  async get(endpoint, options = {}) {
    return this.fetchWithCredentials(endpoint, {
      method: 'GET',
      ...options
    });
  }
  
  // Generic POST method
  async post(endpoint, data, options = {}) {
    return this.fetchWithCredentials(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
      ...options
    });
  }
  
  // Token validation method
  static async validateToken(token) {
    try {
      const apiInstance = new ApiService();
      const result = await apiInstance.post(apiConfig.validateTokenEndpoint, { token });
      return result;
    } catch (error) {
      logger.error('Error validating token:', error);
      return { isValid: false, message: 'Error validating token' };
    }
  }
  
  // Get user profile method
  static async getUserProfile(token) {
    try {
      const apiInstance = new ApiService();
      return await apiInstance.get(apiConfig.userEndpoint, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
    } catch (error) {
      logger.error('Error fetching user profile:', error);
      throw error;
    }
  }
  
  // Session initialization method
  async initSession(userData) {
    return this.post('/session/init', userData);
  }
  
  // Session verification method
  async verifySession() {
    return this.get('/session/verify');
  }
}

export default ApiService;